// ##############################################
// # Copyright (c) 2023 Therigy LLC
// # www.therigy.com
// # All rights reserved
// ##############################################
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Container } from 'react-bootstrap';
import { connect } from 'react-redux';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import Cookies from 'js-cookie';

import './App.css';
import { authService } from './Auth.service';
import TokenManagerService from './services/TokenManagerService';

import Layout from './components/Layout';
import Login from './components/Login';
import ForgotPassword from './components/Login/ForgotPassword';
import ResetPassword from './components/Login/ResetPassword';
import ModelLayout from './components/ModelLayout';
import { Utils } from './shared/utils';
import { loading } from './stores/actions/loader.js';
import IncompleteConfiguration from './components/Login/incompleteConfiguration';

const propTypes = {
    uploadFilesChange: PropTypes.shape({
        uploadFilesPayload: PropTypes.shape({
            uploadFilesData: PropTypes.array,
        }),
    }),
};

class App extends Component {
    constructor(props) {
        super(props);
        this.authService = new authService();
        this._isLoggingOut = false;
        this.state = { authResolved: false };

        this.handleUnload = this.handleUnload.bind(this);

        // Determine initial auth strategy:
        // 1. sso_jwt present → SAML redirect-back; bootstrap refresh cookie
        // 2. No sso_jwt, not logged out, no in-memory token → silent refresh
        //    via httpOnly refresh_token cookie (handles page refresh)
        // 3. Explicitly logged out → skip straight to login
        const ssoToken = Cookies.get('sso_jwt');
        if (ssoToken) {
            this._authPromise = this.authService.loginFromRefreshToken(ssoToken)
                .finally(() => this.setState({ authResolved: true }));
        } else if (!TokenManagerService.isLoggedOut() && !TokenManagerService.getAccessToken()) {
            this._authPromise = this.authService.silentRefresh()
                .finally(() => this.setState({ authResolved: true }));
        } else {
            this.state.authResolved = true;
        }

        const self = this;
        axios.interceptors.request.use(
            (request) => {
                self.props.loading(true);
                if (!request.silent) {
                    //Set app loader
                    document.body.classList.add('loading-indicator');
                }

                const token = TokenManagerService.getAccessToken();
                request.headers['Content-Type'] = 'application/json';
                if (token) {
                    request.headers['Authorization'] = 'Bearer ' + token;
                    request.headers['Pragma'] = 'no-cache';
                }

                // Broadcast activity to other tabs for session-timer sync
                // (mirrors STM-UI's createAxios behavior)
                if (!request.silent) {
                    TokenManagerService.broadcastActivity();
                }

                return request;
            },
            (error) => {
                Promise.reject(error);
            },
        );

        axios.interceptors.response.use(
            (response) => {
                self.props.loading(false);
                //Remove app loader
                document.body.classList.remove('loading-indicator');
                return response;
            },
            async (error) => {
                document.body.classList.remove('loading-indicator');

                // Prevent multiple concurrent logout redirects (mirrors STM-UI's
                // isLoggingOut guard in createAxios.js)
                if (self._isLoggingOut) {
                    return Promise.reject(error);
                }

                // Attempt a silent refresh on 401 before giving up.
                // Skip retry for requests that are already TAS calls (to avoid loops)
                // and skip if this request was already retried.
                const config = error.config || {};
                const isTasCall = config.baseURL && config.baseURL.includes('/tas/');
                if (
                    error.response &&
                    error.response.status === 401 &&
                    !isTasCall &&
                    !config._retried
                ) {
                    config._retried = true;
                    try {
                        const newToken = await TokenManagerService.refreshAccessToken();
                        config.headers['Authorization'] = 'Bearer ' + newToken;
                        return axios(config);
                    } catch (_refreshErr) {
                        self._isLoggingOut = true;
                        self.authService.setLogout();
                        return Promise.reject(error);
                    }
                }
                if (error.response && error.response.status === 401) {
                    self._isLoggingOut = true;
                    self.authService.setLogout();
                }
                return Promise.reject(error);
            },
        );
    }

    render() {
        console.disableYellowBox = true;

        if (!this.state.authResolved) {
            return (
                <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div className="loading-indicator" />
                </div>
            );
        }

        return (
            <Router basename={process.env.PUBLIC_URL}>
                <Container fluid>
                    <Layout>
                        <div className="App">
                            <Switch>
                                <Redirect exact from="/" to="login" />
                                <Route path="/login" component={Login} />
                                <Route path="/model" component={ModelLayout} />
                                <Route path="/forgot-password" component={ForgotPassword} />
                                <Route path="/reset-password" component={ResetPassword} />
                                <Route path="/incompleteConfiguration" component={IncompleteConfiguration} />
                            </Switch>
                        </div>
                    </Layout>
                </Container>
            </Router>
        );
    }

    componentDidMount() {
        const self = this;
        /**
         * Cross-tab logout: listen for FOM_user removal in localStorage.
         * BroadcastChannel (in TokenManagerService) handles token sync across
         * tabs; this covers the case where another tab cleared localStorage.
         */
        window.addEventListener('storage', function (event) {
            if (event.key === 'FOM_user' && event.oldValue && !event.newValue) {
                // if logged out, then force other tabs too
                self.authService.setLogout();
            }
        });

        // Also subscribe to in-memory token clears from BroadcastChannel
        this._unsubToken = TokenManagerService.subscribe(({ access }) => {
            if (!access) {
                self.authService.setLogout();
            } else {
                // Reset the logout guard when a new token arrives (e.g. login in another tab)
                self._isLoggingOut = false;
            }
        });

        window.addEventListener('beforeunload', this.handleUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleUnload);
        if (this._unsubToken) this._unsubToken();
    }

    handleUnload(e) {
        let message = '';
        if (this.props && this.props.uploadFilesChange && this.props.uploadFilesChange.uploadFilesPayload) {
            const uploadFilesData = this.props.uploadFilesChange.uploadFilesPayload.uploadFilesData;
            if (uploadFilesData && uploadFilesData.length > 0) {
                const progress = Utils.summarizeUploadFilesProgress(uploadFilesData);
                if (progress && progress.uploadingFileCount > 0) {
                    message = 'File uploads are pending!';
                    (e || window.event).returnValue = message; //Gecko + IE
                }
            }
        }
        return message;
    }
}

App.propTypes = propTypes;

const mapStateToProps = (state) => {
    return {
        loader: state.loader,
        uploadFilesChange: state.uploadFilesAction,
    };
};

export default connect(mapStateToProps, {
    loading,
})(App);
